
import { defineComponent, computed, PropType } from 'vue'

// Misc
import { isEventSystem, AttrKey } from '@/models/otel'
import { AttrMap, Span, SpanEvent } from '@/models/span'

export interface SpanChip {
  key: string
  value: any
  text: string
}

export default defineComponent({
  name: 'SpanChips',

  props: {
    span: {
      type: Object as PropType<Span>,
      required: true,
    },
    showOperation: {
      type: Boolean,
      default: false,
    },
    traceMode: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const traceId = computed(() => {
      if (props.traceMode) {
        return ''
      }
      if (props.span.standalone) {
        return ''
      }
      return props.span.traceId.slice(-6)
    })
    const traceRoute = computed(() => {
      if (!traceId.value) {
        return ''
      }
      return {
        name: 'TraceShow',
        params: {
          traceId: props.span.traceId,
        },
        query: {
          span: isEventSystem(props.span.system) ? props.span.parentId : props.span.id,
        },
      }
    })

    const events = computed((): SpanEvent[] => {
      return props.span?.events ?? []
    })

    const chips = computed(() => {
      const chips: SpanChip[] = []

      const env = props.span.attrs[AttrKey.deploymentEnvironment]
      if (env) {
        chips.push({ key: AttrKey.deploymentEnvironment, value: env, text: env })
      }

      const service = props.span.attrs[AttrKey.serviceName]
      if (service) {
        chips.push({ key: AttrKey.serviceName, value: service, text: service })
      }

      // Add kind to distinguish `client` and `server` spans.
      if (props.span.kind !== 'internal') {
        chips.push({ key: AttrKey.spanKind, value: props.span.kind, text: props.span.kind })
      }

      if (props.traceMode) {
        const spanSystem = props.span.system
        if (!spanSystem.endsWith(`:${service}`)) {
          chips.push({ key: AttrKey.spanSystem, value: spanSystem, text: spanSystem })
        }
      }

      pushHttpStatusChip(chips, props.span.attrs)

      return chips
    })

    return {
      AttrKey,

      traceId,
      traceRoute,

      events,
      chips,
    }
  },
})

function pushHttpStatusChip(chips: SpanChip[], attrs: AttrMap) {
  const httpCode = attrs[AttrKey.httpStatusCode]
  if (typeof httpCode === 'number' && httpCode != 0 && (httpCode < 200 || httpCode >= 300)) {
    chips.push({ key: AttrKey.httpStatusCode, value: httpCode, text: String(httpCode) })
  }
}
